import type { BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query";
import type {
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import Axios from "axios";
import { axiosNewPrivate } from "./axios";
// No import cycle: accountSlice only imports the (type-only) account API
// types, never this module. accountApiSlice imports axiosNewBaseQuery, but
// the plain accountSlice reducer file does not. Same for
// clearBookingPersonalData: it pulls in bookingCreationSlice + the storage
// manager, neither of which imports any API/axios module.
import { clearAccount } from "../redux/slice/account/accountSlice";
import { clearAccountSessionToken } from "../authentication/accountSession";
import { clearBookingPersonalData } from "./clearBookingPersonalData";

/**
 * H-3a account session expiry (F2): a 401 on the account surface (`/me*`)
 * means the bearer session is dead — drop the stored token and the account
 * slice so guards redirect to login. Deliberately narrow:
 * - never for /auth/otp/confirm (its 401 means "wrong code", handled by the
 *   verify page),
 * - never for the OTP-style 401s on /me/email-change/confirm and DELETE /me
 *   (code invalid_code | expired | too_many_attempts — the session is still
 *   valid, the user just typed a wrong code),
 * - never a page reload or a wholesale localStorage clear (regression test
 *   util/__tests__/axios.test.ts encodes that — see PR #1675).
 */
const OTP_FAILURE_CODES = ["invalid_code", "expired", "too_many_attempts"];

const isAccountSessionExpired = (
  url: string | undefined,
  status: number | undefined,
  data: unknown
): boolean => {
  if (status !== 401) return false;
  // Exactly the account surface: /me and /me/* (not e.g. a future /memberships).
  if (!url || (url !== "/me" && !url.startsWith("/me/"))) return false;
  const code = (data as { code?: string } | null | undefined)?.code;
  return !(typeof code === "string" && OTP_FAILURE_CODES.includes(code.toLowerCase()));
};

export interface AxiosBaseQueryArgs<Meta> {
  meta?: Meta;
  prepareHeaders?: (
    headers: AxiosRequestHeaders,
    api: BaseQueryApi
  ) => AxiosRequestHeaders;
  transformResponse?: (response: Response) => unknown;
}

export interface ServiceExtraOptions {
  authRequired?: boolean;
}

type NewAxiosRequestConfig = AxiosRequestConfig;

const getRequestConfig = (args: string | AxiosRequestConfig) => {
  if (typeof args === "string") {
    return { url: args };
  }

  return args;
};

const axiosNewBaseQuery = <
  Args extends AxiosRequestConfig | string = AxiosRequestConfig,
  Result = unknown,
  DefinitionExtraOptions extends ServiceExtraOptions = Record<string, unknown>,
  Meta = Record<string, unknown>
>({
  prepareHeaders,
  meta,
  transformResponse,
}: AxiosBaseQueryArgs<Meta> = {}): BaseQueryFn<
  Args,
  Result,
  unknown,
  DefinitionExtraOptions,
  Meta
> => {
  return async (args, api, extraOptions) => {
    try {
      const requestConfig: NewAxiosRequestConfig = getRequestConfig(args);

      const result = await axiosNewPrivate({
        ...requestConfig,
        headers: prepareHeaders
          ? prepareHeaders(requestConfig.headers || ({} as any), api)
          : requestConfig.headers,
        signal: api.signal,
        withCredentials: false,
        ...extraOptions,
      });

      return {
        data: transformResponse ? transformResponse(result.data) : result.data,
      };
    } catch (err: any) {

      if (!Axios.isAxiosError(err)) {
        return {
          error: err,
          meta,
        };
      } else {
        if (
          isAccountSessionExpired(
            getRequestConfig(args).url,
            err.response?.status,
            err.response?.data
          )
        ) {
          clearAccountSessionToken();
          api.dispatch(clearAccount());
          // Session end: the expired account's booking-form PII (redux +
          // IDB draft) must not leak to the next user (#1873 scenario 4).
          clearBookingPersonalData(api.dispatch);
        }
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
          meta,
        };
      }
    }
  };
};

export default axiosNewBaseQuery;
